import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import appConfig from 'config/app.config';
import apiHelper from 'helpers/api-helper';
import {getSchoolStats} from 'helpers/stats-helper';
import {sortArrayByProperty} from 'helpers/array-helper';
import {loginUiTexts, adminUiTexts, schoolUiTexts} from 'data/ui-texts';
import Button from 'components/ui/button/button';
import PopupCreateSchool from 'components/admin/popup-create-school/popup-create-school';
import PopupEditSchool from './popup-edit-school/popup-edit-school';
import PopupDeleteSchool from './popup-delete-school/popup-delete-school';
import PopupResetSchool from './popup-reset-school/popup-reset-school';
import './admin.scss';


const Admin = ({handleLogout}) => {
	/* Schools & games */
	const [schools, setSchools] = useState([]);
	const [games, setGames] = useState([]);

	const [orderSchoolsBy, setOrderSchoolsBy] = useState('created');
	const [orderSchoolsDir, setOrderSchoolsDir] = useState('DESC');	

	/* Add school popup */
	const [showAddSchoolPopup, setShowAddSchoolPopup] = useState(false);

	/* Edit school data */
	const [editSchoolData, setEditSchoolData] = useState(null);

	/* Delete school popup */
	const [deleteSchoolData, setDeleteSchoolData] = useState(null);

	/* Reset school plays popup */
	const [resetSchoolData, setResetSchoolData] = useState(null);


	const schoolsWithInfo = schools.map((school) => {
		const schoolGames = games.filter((g) => {return g.schoolId === school.id;});
		const {playthroughCount, studentCount} = getSchoolStats(school, schoolGames);
		const schoolObj = JSON.parse(JSON.stringify(school));
		schoolObj.playthroughCount = playthroughCount;
		schoolObj.studentCount = studentCount;
		schoolObj.nameOrder = schoolObj.name ? schoolObj.name.toLowerCase() : schoolObj.name;
		return schoolObj;
	});
	const orderedSchools = sortArrayByProperty(schoolsWithInfo, orderSchoolsBy, orderSchoolsDir);
	

	/**
	 * Order schools by property
	 * @param {string} property 
	 */
	const handleOrderSchools = (property) => {
		if (orderSchoolsBy === property) {
			/* Just reverse direction */
			const newDirection = (orderSchoolsDir === 'ASC' ? 'DESC' : 'ASC');
			setOrderSchoolsDir(newDirection);
		} else {
			setOrderSchoolsBy(property);
			if (property === 'nameOrder' || property === 'email' || property === 'password') {
				setOrderSchoolsDir('ASC');	
			} else {
				setOrderSchoolsDir('DESC');
			}
		}
	};


	/**
	 * Create new school
	 * @param {string} name 
	 * @param {string} email 
	 * @param {string} password 
	 * @param {array} scenarioIds
	 * @param {array} scenarioQrUrls
	 * @param {array} questions
	 * @param {string} facilitatorSurveyLink
	 * @returns 
	 */
	const createSchool = (name, email, password, scenarioIds, 
		scenarioQrUrls, questions, facilitatorSurveyLink) => {
		return new Promise((resolve) => {
			apiHelper('admin/create-school', {
				name, email, password, scenarioIds, 
				scenarioQrUrls, questions, facilitatorSurveyLink
			}).then(() => {
				resolve({status: 'success'});
			}).catch((error) => {
				console.error(error);
				resolve({status: 'error', error: error});
			});
		});
	};

	/**
	 * Edit school
	 * @param {string} schoolId
	 * @param {string} name
	 * @param {string} email
	 * @param {string} password
	 * @param {string} scenarioIds
	 * @param {array} scenarioQrUrls
	 * @param {array} questions
	 * @param {string} facilitatorSurveyLink
	 * @param {boolean} showLinkToShortHTX
	 * @param {boolean} showLinkToShortHHX
	 * @param {string} customAreaId
	 * @param {string} backgroundImage
	 * @returns
	 */
	const editSchool = (schoolId, name, email, password, scenarioIds,
		scenarioQrUrls, questions, facilitatorSurveyLink, showLinkToShortHTX, showLinkToShortHHX,
		customAreaId, backgroundImage) => {
		return new Promise((resolve) => {
			apiHelper('admin/edit-school', {
				schoolId, name, email, password, scenarioIds,
				scenarioQrUrls, questions, facilitatorSurveyLink, showLinkToShortHTX, showLinkToShortHHX,
				customAreaId, backgroundImage
			}).then(() => {
				resolve({status: 'success'});
			}).catch((error) => {
				console.error(error);
				resolve({status: 'error', error: error});
			});
		});
	};

	/**
	 * Delete school
	 * @param {string} schoolId
	 * @returns 
	 */
	const deleteSchool = (schoolId) => {
		return new Promise((resolve) => {
			apiHelper('admin/delete-school', {schoolId}).then(() => {
				resolve({status: 'success'});
			}).catch((error) => {
				console.error(error);
				resolve({status: 'error', error: error});
			});
		});
	};

	/**
	 * Component did mount / will unmount
	 */
	useEffect(() => {
		const db = firebase.firestore();
		
		/* Subscribe to schools */
		const unsubscribeSchools = db.collection(appConfig.schoolsDbName).onSnapshot(
			(querySnapshot) => {
				const dbSchools = [];
				querySnapshot.forEach((doc) => {dbSchools.push({id: doc.id, ...doc.data()});});
				setSchools(dbSchools);
			},
			(error) => {
				console.error('could not get schools: ', error);
			}
		);

		/* Subscribe to games */
		const unsubscribeGames = db.collection(appConfig.gamesDbName).onSnapshot(
			(querySnapshot) => {
				const dbGames = [];
				querySnapshot.forEach((doc) => {dbGames.push({id: doc.id, ...doc.data()});});
				setGames(dbGames);
			},
			(error) => {
				console.error('could not get games: ', error);
			}
		);


		return () => {
			/* Cancel subscriptions */
			unsubscribeSchools();	
			unsubscribeGames();
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="Admin">
			<div className="Admin-title">
				<span>{schoolUiTexts.admin}</span>
			</div>
			<div className='Admin-schools'>
				{/* Create new school btn */}
				<div className='Admin-createSchoolBtn'>
					<Button
						classes={['login']}
						text={adminUiTexts.createSchool}
						onClick={() => {setShowAddSchoolPopup(true);}}
					/>
				</div>
				{/* List of schools */}
				<div className='Admin-row header'>
					<div 
						className={'Admin-item name orderBy' + (orderSchoolsBy === 'nameOrder' ? ' selected' : '')}
						onClick={() => {handleOrderSchools('nameOrder');}}
					>
						<span>{adminUiTexts.schoolList.school}</span>
					</div>
					<div 
						className={'Admin-item email orderBy' + (orderSchoolsBy === 'email' ? ' selected' : '')}
						onClick={() => {handleOrderSchools('email');}}
					>
						<span>{adminUiTexts.schoolList.email}</span>
					</div>
					<div 
						className={'Admin-item password orderBy' + (orderSchoolsBy === 'password' ? ' selected' : '')}
						onClick={() => {handleOrderSchools('password');}}
					>
						<span>{adminUiTexts.schoolList.password}</span>
					</div>
					<div 
						className={'Admin-item students orderBy' 
							+ (orderSchoolsBy === 'studentCount' ? ' selected' : '')}
						onClick={() => {handleOrderSchools('studentCount');}}
					>
						<span>{adminUiTexts.schoolList.students}</span>
					</div>
					<div 
						className={'Admin-item plays orderBy' 
							+ (orderSchoolsBy === 'playthroughCount' ? ' selected' : '')}
						onClick={() => {handleOrderSchools('playthroughCount');}}
					>
						<span>{adminUiTexts.schoolList.playthroughs}</span>
					</div>
					<div className='Admin-item edit'>
						<span>{adminUiTexts.schoolList.edit}</span>
					</div>
					<div className='Admin-item reset'>
						<span>{adminUiTexts.schoolList.reset}</span>
					</div>
					<div className='Admin-item delete'>
						<span>{adminUiTexts.schoolList.delete}</span>
					</div>
				</div>

				{orderedSchools && <div className="Admin-rows">
					{orderedSchools.map((school, index) => {
						const schoolGames = games.filter((g) => {return g.schoolId === school.id;});
						const {playthroughCount, studentCount} = getSchoolStats(school, schoolGames);
						return (
							<div key={index} className='Admin-row body'>
								<div className='Admin-item name'>
									<span>{school.name}</span>
								</div>
								<div className='Admin-item email'>
									<span>{school.email}</span>
								</div>
								<div className='Admin-item password'>
									<span>{school.password}</span>
								</div>
								<div className='Admin-item students'>
									<span>{studentCount}</span>
								</div>
								<div className='Admin-item plays'>
									<span>{playthroughCount}</span>
								</div>

								<div className='Admin-item edit' onClick={() => {setEditSchoolData(school);}}/>
								<div className='Admin-item reset' onClick={() => {setResetSchoolData(school);}}/>
								<div className='Admin-item delete' onClick={() => {setDeleteSchoolData(school);}}/>
							</div>
						);
					})} 
				</div>}
			</div>

			{/* Logout button */}
			<div className="Admin-logoutBtn">
				<Button 
					classes={['login', 'logout']} 
					text={loginUiTexts.logout}
					onClick={() => {handleLogout();}}
				/>
			</div>

			{/* Create school popup */}
			{showAddSchoolPopup && <PopupCreateSchool 
				schools={schools}
				createSchool={createSchool}
				closePopup={() => {setShowAddSchoolPopup(false);}} 
			/>}

			{/* Edit school popup */}
			{editSchoolData && <PopupEditSchool 
				school={editSchoolData}
				schools={schools}
				editSchool={editSchool}
				closePopup={() => {setEditSchoolData(false);}} 
			/>}

			{/* Delete school popup */}
			{deleteSchoolData && <PopupDeleteSchool 
				schoolData={deleteSchoolData} 
				deleteSchool={deleteSchool} 
				closePopup={() => {setDeleteSchoolData(null);}} 
			/>}

			{resetSchoolData && <PopupResetSchool 
				schoolData={resetSchoolData} 
				games={games}
				closePopup={() => {setResetSchoolData(null);}} 
			/>}


		</div>
	);
};

Admin.propTypes = {
	handleLogout: PropTypes.func.isRequired,
};

export default Admin;
